/*
 * RecommendationPill — compact tab welded to the top of the composer.
 *
 * Two modes, picked from the central route plan:
 *
 *   1. Recommend mode (no approach picked yet):
 *      Collapsed: "RECOMMENDED · <approach> ⌄"
 *      Expanded:  three mini-cards side-by-side, recommended one badged.
 *      Same content as the spec's Scoping Approach Choice Screen,
 *      miniaturized into the composer.
 *
 *   2. Resume mode (approach already picked):
 *      A single-click pill: "SELECTED · <approach> · Open Orbit Scope →".
 *      Tapping fires onResume which navigates to route_plan.next_best_action.route.
 *      Keeps the affordance present so the user always has a way back to
 *      where they were going — never strands them on /orbit/intake.
 *
 * Hidden only when there's no actionable state (no suggestion AND no
 * selected_approach yet).
 */

import React, { useState } from 'react';
import {
  ArrowRight,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  CheckCircle2,
  Zap,
  Users,
  Compass,
} from 'lucide-react';

import type { Approach, RoutePlan, SuggestionResult } from '../lib/dossier-types';


export interface RecommendationPillProps {
  plan: RoutePlan | null | undefined;
  suggestion: SuggestionResult | null | undefined;
  onPick: (approach: Approach) => void;
  picking?: Approach | null;
  // Called when the user clicks the resume pill (post-pick state).
  // Parent navigates to route_plan.next_best_action.route.
  onResume?: () => void;
}


interface ApproachCard {
  approach: Approach;
  short: string;
  full: string;
  icon: React.ReactNode;
  speed: string;
  price: string;
  fit: string;
}

// v0.5 — Only Solution Partner is shown as an approach card.
// DIY and Explore were dropped per product call: DIY is implicit (the
// user just keeps scoping with Orbit), Explore lives in the "Skip to
// dashboard" topbar pill. No 3-card chooser anywhere.
const APPROACH_CARDS: ApproachCard[] = [
  {
    approach: 'paid_human',
    short: 'Solution Partner',
    full: 'Solution Partner scoping',
    icon: <Users size={14} strokeWidth={2.25} />,
    speed: '2–5 sessions',
    price: 'Premium',
    fit: 'Regulated, complex, burned',
  },
];


export function RecommendationPill({
  plan,
  suggestion,
  onPick,
  picking,
  onResume,
}: RecommendationPillProps) {
  const [open, setOpen] = useState(false);

  // ── Resume mode — approach already picked. Single-click affordance to
  //    get the user back to wherever next_best_action points.
  const selected = plan?.selected_approach as Approach | null | undefined;
  if (selected) {
    const card = APPROACH_CARDS.find(c => c.approach === selected);
    const recommended = plan?.recommended_path as Approach | null | undefined;
    const recCard = recommended ? APPROACH_CARDS.find(c => c.approach === recommended) : null;
    const routeDrift = Boolean(plan?.route_drift && recCard && recCard.approach !== selected);
    const nba = plan?.next_best_action;
    if (!card || !nba?.route || !onResume) return null;
    if (routeDrift && recCard) {
      return (
        <div
          className="aa-rec-pill aa-rec-pill--resume aa-rec-pill--drift"
          role="region"
          aria-label="Route changed"
        >
          <button
            type="button"
            className="aa-rec-pill-drift-head"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
          >
            <span className="aa-rec-pill-leading">
              <AlertTriangle size={11} strokeWidth={2.5} />
              <span className="aa-rec-pill-eyebrow">Route changed</span>
            </span>
            <span className="aa-rec-pill-mid">
              {card.icon}
              <span className="aa-rec-pill-approach">{card.short} → {recCard.short}</span>
            </span>
            <span className="aa-rec-pill-resume-cta">
              <span>Review choices</span>
              {open ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
            </span>
          </button>

          <div className="aa-rec-pill-drift-reason">
            {plan?.route_drift_reason}
          </div>

          {open && (
            <div className="aa-rec-pill-body aa-rec-pill-body--drift">
              <div className="aa-rec-pill-cards" role="group" aria-label="Pick a scoping approach">
                {APPROACH_CARDS.map(option => {
                  const isRec = option.approach === recommended;
                  const isSelected = option.approach === selected;
                  const isPicking = picking === option.approach;
                  return (
                    <button
                      type="button"
                      key={option.approach}
                      className={`aa-rec-pill-card${isRec ? ' aa-rec-pill-card--rec' : ''}`}
                      onClick={() => onPick(option.approach)}
                      disabled={!!picking || isSelected}
                      aria-pressed={isSelected}
                    >
                      {isRec && (
                        <span className="aa-rec-pill-card-badge">
                          <Sparkles size={10} strokeWidth={2.5} /> Recommended
                        </span>
                      )}
                      <span className="aa-rec-pill-card-head">
                        <span className="aa-rec-pill-card-icon">{option.icon}</span>
                        <span className="aa-rec-pill-card-title">{option.full}</span>
                      </span>
                      <dl className="aa-rec-pill-card-meta">
                        <div><dt>Speed</dt><dd>{option.speed}</dd></div>
                        <div><dt>Price</dt><dd>{option.price}</dd></div>
                        <div><dt>Fits</dt><dd>{option.fit}</dd></div>
                      </dl>
                      <span className="aa-rec-pill-card-cta">
                        {isSelected ? 'Current' : isPicking ? 'Routing…' : 'Pick'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="aa-rec-pill-continue"
                onClick={onResume}
              >
                <span>Continue with current path</span>
                <ArrowRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      );
    }
    return (
      <button
        type="button"
        className={`aa-rec-pill aa-rec-pill--resume${routeDrift ? ' aa-rec-pill--drift' : ''}`}
        onClick={onResume}
        aria-label={`${nba.label} — ${card.full} selected`}
      >
        <span className="aa-rec-pill-leading">
          {routeDrift ? <AlertTriangle size={11} strokeWidth={2.5} /> : <CheckCircle2 size={11} strokeWidth={2.5} />}
          <span className="aa-rec-pill-eyebrow">{routeDrift ? 'Route changed' : 'Selected'}</span>
        </span>
        <span className="aa-rec-pill-mid">
          {card.icon}
          <span className="aa-rec-pill-approach">
            {routeDrift && recCard ? `${card.short} → ${recCard.short}` : card.short}
          </span>
        </span>
        <span className="aa-rec-pill-resume-cta">
          <span>{nba.label}</span>
          <ArrowRight size={14} strokeWidth={2.5} />
        </span>
      </button>
    );
  }

  // ── Recommend mode — no approach picked yet.
  // Per v2.7 spec the Suggestion Generator only fires after L1 capture
  // is complete (all required_for_routing fields landed). The backend's
  // generate_suggestion already returns None until then, so `suggestion`
  // will normally be null mid-L1. The readiness check below is belt-
  // and-braces against a stale suggestion from an earlier turn.
  if (!suggestion || !plan) return null;
  if (!plan.readiness?.l1_complete) return null;
  const recommended = suggestion.approach as Approach;
  const recCard = APPROACH_CARDS.find(c => c.approach === recommended);
  if (!recCard) return null;

  return (
    <div className={`aa-rec-pill${open ? ' aa-rec-pill--open' : ''}`} role="region" aria-label="Recommended approach">
      <button
        type="button"
        className="aa-rec-pill-trigger"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="aa-rec-pill-leading">
          <Sparkles size={11} strokeWidth={2.5} />
          <span className="aa-rec-pill-eyebrow">Recommended</span>
        </span>
        <span className="aa-rec-pill-mid">
          {recCard.icon}
          <span className="aa-rec-pill-approach">{recCard.short}</span>
        </span>
        <span className="aa-rec-pill-toggle">
          {open ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
        </span>
      </button>

      {open && (
        <div className="aa-rec-pill-body">
          <p className="aa-rec-pill-line">{suggestion.suggestion_line}</p>
          {suggestion.messaging_override && (
            <p className="aa-rec-pill-override">{suggestion.messaging_override}</p>
          )}

          <div className="aa-rec-pill-cards" role="group" aria-label="Pick a scoping approach">
            {APPROACH_CARDS.map(card => {
              const isRec = card.approach === recommended;
              const isPicking = picking === card.approach;
              return (
                <button
                  type="button"
                  key={card.approach}
                  className={`aa-rec-pill-card${isRec ? ' aa-rec-pill-card--rec' : ''}`}
                  onClick={() => onPick(card.approach)}
                  disabled={!!picking}
                  aria-pressed={isRec}
                >
                  {isRec && (
                    <span className="aa-rec-pill-card-badge">
                      <Sparkles size={10} strokeWidth={2.5} /> Recommended
                    </span>
                  )}
                  <span className="aa-rec-pill-card-head">
                    <span className="aa-rec-pill-card-icon">{card.icon}</span>
                    <span className="aa-rec-pill-card-title">{card.full}</span>
                  </span>
                  <dl className="aa-rec-pill-card-meta">
                    <div><dt>Speed</dt><dd>{card.speed}</dd></div>
                    <div><dt>Price</dt><dd>{card.price}</dd></div>
                    <div><dt>Fits</dt><dd>{card.fit}</dd></div>
                  </dl>
                  <span className="aa-rec-pill-card-cta">
                    {isPicking ? 'Routing…' : 'Pick'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default RecommendationPill;
